import { useEffect, useMemo } from "react";
import { QueryClient, QueryClientProvider } from "react-query";
import { useSelector } from "react-redux";

import {
  ApolloClient,
  ApolloProvider,
  InMemoryCache,
  createHttpLink,
  makeVar
} from "@apollo/client";
import { setContext } from "@apollo/client/link/context";
import { preCache } from "@zumer/snapdom";
import { ConfigProvider } from "antd";
import { RootState } from "store/rootReducer";
import { checkTokenExpire } from "utils";

import { apiRequestHeadersFor } from "api/apiRequestHeaders";
import { refreshToken } from "api/auth";

import { ShapefileProvider } from "components/project/shapefiles/context";
import { PostProvider } from "components/user-documentation";
import { UserProvider } from "components/user/context";

ConfigProvider.config({
  theme: {
    primaryColor: "#041C2C"
  }
});

type AppProviderPropsT = {
  // currentJwtToken: string;
  children: React.ReactNode | React.ReactNode[] | null;
};

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      refetchOnWindowFocus: false
    }
  }
});

// Create reactive variables to store auth state
/** A GraphQL service link, marked as an API call for Novi's edge when it shares the page's origin. */
const apiLink = (uri: Maybe<string>) =>
  createHttpLink({ uri, headers: apiRequestHeadersFor(uri) });

export const jwtTokenVar = makeVar<string | null>(null);
export const broadcastTokenVar = makeVar<string | null>(null);
export const tokenRefreshingVar = makeVar<boolean>(false);

// Warm the snapdom font cache at startup so the first screenshot doesn't stall
// waiting to fetch and inline @font-face sources.
preCache(document.body, { embedFonts: true });

const AppProvider = ({
  children = null
}: // currentJwtToken
AppProviderPropsT): JSX.Element => {
  const currentJwtToken = useSelector((state: RootState) => state.auth.jwtToken);
  const user = useSelector((state: RootState) => state.auth.user);
  const tokenRefreshing = useSelector((state: RootState) => state.auth.tokenRefreshing);

  // Update reactive variables when auth state changes
  useEffect(() => {
    jwtTokenVar(currentJwtToken);
    broadcastTokenVar(user?.sessionBroadcastToken || null);
    tokenRefreshingVar(tokenRefreshing);
  }, [currentJwtToken, user?.sessionBroadcastToken, tokenRefreshing]);

  const waitForNewToken = async (oldToken) => {
    let newToken = jwtTokenVar();
    const maxWaitTime = 60000; // 1 minute in milliseconds
    const startTime = Date.now();

    while (newToken === oldToken && Date.now() - startTime < maxWaitTime) {
      await new Promise((resolve) => setTimeout(resolve, 100)); // Wait for 100ms
      newToken = jwtTokenVar();
    }

    return newToken;
  };

  const apolloClient = useMemo(() => {
    //useMemo to make sure apolloClient only created once

    const userArpsLink = apiLink(process.env.REACT_APP_TYPE_WELL_GQL_SERVICE);
    const savedFiltersLink = apiLink(process.env.REACT_APP_SAVED_FILTER_GQL_SERVICE);
    const workspacesLink = apiLink(process.env.REACT_APP_WORKSPACE_SERVICE);

    const authLink = setContext(async (_, { headers }) => {
      const jwtToken = jwtTokenVar();

      if (!jwtToken) {
        return { headers };
      }

      if (!checkTokenExpire(jwtToken)) {
        return {
          headers: {
            ...headers,
            authorization: `Bearer ${jwtToken}`
          }
        };
      } else {
        try {
          if (tokenRefreshingVar()) {
            // wait here until the new token is set
            const newTokenFromOtherTab = await waitForNewToken(jwtToken);
            return {
              headers: {
                ...headers,
                authorization: `Bearer ${newTokenFromOtherTab}`
              }
            };
          }

          const newToken = await refreshToken(broadcastTokenVar());
          if (newToken === null) {
            // wait here until the new token is set
            const newTokenFromOtherTab = await waitForNewToken(jwtToken);
            return {
              headers: {
                ...headers,
                authorization: `Bearer ${newTokenFromOtherTab}`
              }
            };
          }

          return {
            headers: {
              ...headers,
              authorization: `Bearer ${newToken}`
            }
          };
        } catch {
          throw new Error("Token refresh failed");
        }
      }
    });

    const splitLink = authLink.split(
      (operation) => operation.getContext().clientName === "saved-filters",
      savedFiltersLink,
      authLink.split(
        (operation) => operation.getContext().clientName === "workspaces",
        workspacesLink,
        userArpsLink
      )
    );

    return new ApolloClient({
      link: splitLink,
      cache: new InMemoryCache(),
      defaultOptions: {
        mutate: {
          fetchPolicy: "no-cache" // don't cache mutation results to prevent memory leaks
        }
      }
    });
  }, []); // Empty dependency array - create Apollo client only once

  return (
    <ApolloProvider client={apolloClient}>
      <QueryClientProvider client={queryClient}>
        <ConfigProvider>
          <UserProvider>
            <ShapefileProvider>
              <PostProvider>{children}</PostProvider>
            </ShapefileProvider>
          </UserProvider>
        </ConfigProvider>
      </QueryClientProvider>
    </ApolloProvider>
  );
};

export default AppProvider;
